"use client";

import {
  FavoriteHotel,
  FavoriteHotelCollectionFromJSON,
  FavoriteHotelFromJSON,
  FavoriteHotelMembershipResponseFromJSON,
  FavoriteHotelMutationResponseFromJSON,
} from "@/generated/search.openapi";
import {
  captureFavoriteEvent,
  FAVORITE_ADD,
  FAVORITE_REMOVE,
  FavoriteSurface,
} from "@/lib/analytics/hotelFavorites";
import { useGlobalStore } from "@/app/context/globalStoreProvider";
import { errorToast } from "@/lib/toast";
import { useSession } from "next-auth/react";
import { usePostHog } from "posthog-js/react";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

const MEMBERSHIP_CHUNK_SIZE = 100;
const HISTORY_PAGE_SIZE = 50;
// Background retries for a failed search-membership chunk. While retries remain
// the search reports "loading", so the UI keeps waiting instead of showing a
// false empty result.
export const FAVORITE_MEMBERSHIP_RETRY_DELAYS_MS: readonly number[] = [
  1000, 3000,
];
export const FAVORITE_MUTATION_ERROR = "Couldn’t update favorite. Try again.";

type HistoryStatus = "disabled" | "idle" | "loading" | "ready" | "error";

export type FavoriteHistoryOperation =
  | { kind: "refresh" }
  | { kind: "pagination"; cursor: string };

export type FavoriteHistoryState = {
  status: HistoryStatus;
  items: FavoriteHotel[];
  nextCursor: string | null;
  failedOperation: FavoriteHistoryOperation | null;
};

type MembershipEntry = {
  confirmed?: boolean;
  confirmedGeneration: number;
  desired?: boolean;
  generation: number;
  inFlight: boolean;
  surface?: FavoriteSurface;
};

export type HotelFavoriteState = {
  isFavorite: boolean | undefined;
  pending: boolean;
};

export type FavoriteSearchMembershipStatus =
  | "disabled"
  | "idle"
  | "loading"
  | "ready"
  | "error";

export type FavoriteSearchMembershipState = {
  status: FavoriteSearchMembershipStatus;
  isComplete: boolean;
  isTerminal: boolean;
  effectiveFavoriteIds: ReadonlySet<number>;
  candidateIds: ReadonlySet<number>;
};

type SearchMembershipRegistry = {
  key?: string;
  generation: number;
  canonicalized: boolean;
  candidateIds: Set<number>;
  resolved: Map<number, boolean>;
  loading: Set<number>;
  failed: Set<number>;
  retryAttempts: number;
  terminal: boolean;
};

type FavoriteHotelsContextValue = {
  enabled: boolean;
  subject: string | undefined;
  history: FavoriteHistoryState;
  error: string | null;
  ensureMembership: (propertyIds: number[]) => Promise<void>;
  searchMembership: FavoriteSearchMembershipState;
  registerSearchCandidates: (
    searchKey: string,
    propertyIds: number[],
    options?: { terminal?: boolean; replace?: boolean }
  ) => void;
  retrySearchMembership: () => void;
  resetSearchMembership: () => void;
  refreshHistory: () => Promise<void>;
  loadMoreHistory: () => Promise<void>;
  retryHistory: () => Promise<void>;
  favoriteState: (propertyId: number) => HotelFavoriteState;
  toggleFavorite: (propertyId: number, surface: FavoriteSurface) => void;
};

const DISABLED_HISTORY: FavoriteHistoryState = {
  status: "disabled",
  items: [],
  nextCursor: null,
  failedOperation: null,
};

const FavoriteHotelsContext = createContext<FavoriteHotelsContextValue | null>(
  null
);

function reconcileHistoryItems(
  currentItems: FavoriteHotel[],
  responseItems: FavoriteHotel[],
  append: boolean,
  confirmedGenerations: ReadonlyMap<number, number>,
  currentMembership: ReadonlyMap<number, MembershipEntry>
): FavoriteHotel[] {
  let items = append ? [...currentItems, ...responseItems] : [...responseItems];

  currentMembership.forEach((entry, propertyId) => {
    const requestGeneration = confirmedGenerations.get(propertyId) ?? 0;
    if (entry.confirmedGeneration <= requestGeneration) return;

    const currentItem = currentItems.find(
      (item) => item.propertyId === propertyId
    );
    const responseItem = responseItems.find(
      (item) => item.propertyId === propertyId
    );
    items = items.filter((item) => item.propertyId !== propertyId);
    if (entry.confirmed) {
      const favorite = currentItem ?? responseItem;
      if (favorite) items.unshift(favorite);
    }
  });

  const seen = new Set<number>();
  return items.filter((item) => {
    if (seen.has(item.propertyId)) return false;
    seen.add(item.propertyId);
    return true;
  });
}

function seedHistoryMembership(
  responseItems: FavoriteHotel[],
  requestGenerations: ReadonlyMap<number, number>,
  requestConfirmedGenerations: ReadonlyMap<number, number>,
  currentMembership: Map<number, MembershipEntry>
): void {
  responseItems.forEach((item) => {
    const existing = currentMembership.get(item.propertyId);
    const requestGeneration = requestGenerations.get(item.propertyId) ?? 0;
    const requestConfirmedGeneration =
      requestConfirmedGenerations.get(item.propertyId) ?? 0;
    if (
      (existing?.generation ?? 0) !== requestGeneration ||
      (existing?.confirmedGeneration ?? 0) !== requestConfirmedGeneration ||
      existing?.desired !== undefined ||
      existing?.inFlight
    ) {
      return;
    }

    const generation = requestGeneration + 1;
    currentMembership.set(item.propertyId, {
      confirmed: true,
      confirmedGeneration: requestConfirmedGeneration,
      generation,
      inFlight: false,
      surface: existing?.surface,
    });
  });
}

function resolveKnownSearchCandidates(
  registry: SearchMembershipRegistry,
  currentMembership: ReadonlyMap<number, MembershipEntry>,
  propertyIds: readonly number[] | ReadonlySet<number>
): void {
  propertyIds.forEach((propertyId) => {
    if (!registry.candidateIds.has(propertyId)) return;
    const confirmed = currentMembership.get(propertyId)?.confirmed;
    if (confirmed === undefined) return;
    registry.resolved.set(propertyId, confirmed);
    registry.failed.delete(propertyId);
  });
}

export function AuthenticatedFavoriteHotelsBoundary({
  children,
}: PropsWithChildren): JSX.Element {
  return <FavoriteHotelsProvider>{children}</FavoriteHotelsProvider>;
}

export function FavoriteHotelsProvider({
  children,
  membershipRetryDelaysMs = FAVORITE_MEMBERSHIP_RETRY_DELAYS_MS,
}: PropsWithChildren<{
  membershipRetryDelaysMs?: readonly number[];
}>): JSX.Element {
  const { data: session, status: sessionStatus } = useSession();
  const { user: effectiveUser } = useGlobalStore();
  const posthog = usePostHog();
  const subject = effectiveUser?.data.id;
  const enabled =
    sessionStatus === "authenticated" &&
    Boolean(session?.user?.id) &&
    Boolean(subject);
  const identity = `${enabled}:${subject ?? ""}`;

  const sessionGeneration = useRef(0);
  const storeIdentity = useRef(identity);
  const enabledRef = useRef(enabled);
  const membership = useRef(new Map<number, MembershipEntry>());
  const membershipLoading = useRef(new Set<number>());
  const historyRequests = useRef(new Set<string>());
  const searchRegistry = useRef<SearchMembershipRegistry>({
    generation: 0,
    canonicalized: false,
    candidateIds: new Set(),
    resolved: new Map(),
    loading: new Set(),
    failed: new Set(),
    retryAttempts: 0,
    terminal: false,
  });
  const posthogRef = useRef(posthog);
  const [, forceRender] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<FavoriteHistoryState>(
    enabled ? { ...DISABLED_HISTORY, status: "idle" } : DISABLED_HISTORY
  );
  const [stateIdentity, setStateIdentity] = useState(identity);

  if (stateIdentity !== identity) {
    setStateIdentity(identity);
    setError(null);
    setHistory(
      enabled ? { ...DISABLED_HISTORY, status: "idle" } : DISABLED_HISTORY
    );
  }

  const rerender = useCallback(() => {
    forceRender((version) => version + 1);
  }, []);

  const fetchHistory = useCallback(async (cursor?: string): Promise<void> => {
    if (!enabledRef.current) return;
    const generation = sessionGeneration.current;
    const membershipGenerations = new Map(
      Array.from(membership.current, ([propertyId, entry]) => [
        propertyId,
        entry.generation,
      ])
    );
    const confirmedGenerations = new Map(
      Array.from(membership.current, ([propertyId, entry]) => [
        propertyId,
        entry.confirmedGeneration,
      ])
    );
    const requestKey = `${generation}:${cursor ?? ""}`;
    if (historyRequests.current.has(requestKey)) return;
    historyRequests.current.add(requestKey);
    setHistory((current) => ({
      ...current,
      status: "loading",
      failedOperation: null,
    }));

    const params = new URLSearchParams({
      limit: String(HISTORY_PAGE_SIZE),
    });
    if (cursor) params.set("cursor", cursor);

    try {
      const response = await fetch(
        `/api/search/hotel/favorites?${params.toString()}`,
        { cache: "no-store" }
      );
      if (!response.ok) throw new Error("favorite history request failed");
      const result = FavoriteHotelCollectionFromJSON(await response.json());
      if (generation !== sessionGeneration.current || !enabledRef.current) {
        return;
      }
      seedHistoryMembership(
        result.items,
        membershipGenerations,
        confirmedGenerations,
        membership.current
      );
      resolveKnownSearchCandidates(
        searchRegistry.current,
        membership.current,
        result.items.map((item) => item.propertyId)
      );
      setHistory((current) => ({
        status: "ready",
        items: reconcileHistoryItems(
          current.items,
          result.items,
          Boolean(cursor),
          confirmedGenerations,
          membership.current
        ),
        nextCursor: result.nextCursor,
        failedOperation: null,
      }));
    } catch {
      if (generation === sessionGeneration.current && enabledRef.current) {
        setHistory((current) => ({
          ...current,
          status: "error",
          failedOperation: cursor
            ? { kind: "pagination", cursor }
            : { kind: "refresh" },
        }));
      }
    } finally {
      historyRequests.current.delete(requestKey);
    }
  }, []);

  const refreshHistory = useCallback(() => fetchHistory(), [fetchHistory]);

  const loadMoreHistory = useCallback(async (): Promise<void> => {
    if (history.nextCursor) {
      await fetchHistory(history.nextCursor);
    }
  }, [fetchHistory, history.nextCursor]);

  const retryHistory = useCallback(async (): Promise<void> => {
    const failedOperation = history.failedOperation;
    if (!failedOperation) return;
    await fetchHistory(
      failedOperation.kind === "pagination" ? failedOperation.cursor : undefined
    );
  }, [fetchHistory, history.failedOperation]);

  const ensureMembership = useCallback(
    async (propertyIds: number[]): Promise<void> => {
      if (!enabledRef.current) return;
      const uniqueIds = Array.from(new Set(propertyIds)).filter(
        (propertyId) =>
          Number.isSafeInteger(propertyId) &&
          propertyId > 0 &&
          membership.current.get(propertyId)?.confirmed === undefined &&
          !membershipLoading.current.has(propertyId)
      );
      const generation = sessionGeneration.current;
      uniqueIds.forEach((propertyId) =>
        membershipLoading.current.add(propertyId)
      );

      for (
        let offset = 0;
        offset < uniqueIds.length;
        offset += MEMBERSHIP_CHUNK_SIZE
      ) {
        const chunk = uniqueIds.slice(offset, offset + MEMBERSHIP_CHUNK_SIZE);
        const propertyGenerations = new Map(
          chunk.map((propertyId) => [
            propertyId,
            membership.current.get(propertyId)?.generation ?? 0,
          ])
        );
        try {
          const response = await fetch(
            "/api/search/hotel/favorites/membership",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ propertyIds: chunk }),
              cache: "no-store",
            }
          );
          if (!response.ok) throw new Error("favorite membership failed");
          const result = FavoriteHotelMembershipResponseFromJSON(
            await response.json()
          );
          if (generation !== sessionGeneration.current || !enabledRef.current) {
            continue;
          }
          const favorites = new Set(result.propertyIds);
          for (const propertyId of chunk) {
            const existing = membership.current.get(propertyId);
            if (
              (existing?.generation ?? 0) !==
              propertyGenerations.get(propertyId)
            ) {
              continue;
            }
            membership.current.set(propertyId, {
              confirmed: favorites.has(propertyId),
              confirmedGeneration: existing?.confirmedGeneration ?? 0,
              generation: (existing?.generation ?? 0) + 1,
              inFlight: existing?.inFlight ?? false,
              desired: existing?.desired,
              surface: existing?.surface,
            });
          }
          resolveKnownSearchCandidates(
            searchRegistry.current,
            membership.current,
            chunk
          );
          rerender();
        } catch {
          // Preserve prior confirmed/unknown membership on read failure.
        } finally {
          chunk.forEach((propertyId) =>
            membershipLoading.current.delete(propertyId)
          );
        }
      }
    },
    [rerender]
  );

  const resolveSearchMembership = useCallback(
    async (propertyIds: number[], generation: number): Promise<void> => {
      const chunks: number[][] = [];
      for (
        let offset = 0;
        offset < propertyIds.length;
        offset += MEMBERSHIP_CHUNK_SIZE
      ) {
        chunks.push(propertyIds.slice(offset, offset + MEMBERSHIP_CHUNK_SIZE));
      }

      await Promise.all(
        chunks.map(async (chunk) => {
          const registry = searchRegistry.current;
          if (
            generation !== registry.generation ||
            !enabledRef.current ||
            chunk.length === 0
          ) {
            return;
          }
          const propertyGenerations = new Map(
            chunk.map((propertyId) => [
              propertyId,
              membership.current.get(propertyId)?.generation ?? 0,
            ])
          );
          chunk.forEach((propertyId) => {
            registry.loading.add(propertyId);
            registry.failed.delete(propertyId);
          });
          rerender();

          try {
            const response = await fetch(
              "/api/search/hotel/favorites/membership",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ propertyIds: chunk }),
                cache: "no-store",
              }
            );
            if (!response.ok) throw new Error("favorite membership failed");
            const result = FavoriteHotelMembershipResponseFromJSON(
              await response.json()
            );
            const requested = new Set(chunk);
            if (
              result.propertyIds.some(
                (propertyId) =>
                  !Number.isSafeInteger(propertyId) ||
                  propertyId <= 0 ||
                  !requested.has(propertyId)
              )
            ) {
              throw new Error("malformed favorite membership response");
            }
            const current = searchRegistry.current;
            if (generation !== current.generation || !enabledRef.current) {
              return;
            }
            const favorites = new Set(result.propertyIds);
            for (const propertyId of chunk) {
              if (!current.candidateIds.has(propertyId)) continue;
              const existing = membership.current.get(propertyId);
              const expectedGeneration = propertyGenerations.get(propertyId);
              if ((existing?.generation ?? 0) === expectedGeneration) {
                const confirmed = favorites.has(propertyId);
                current.resolved.set(propertyId, confirmed);
                membership.current.set(propertyId, {
                  confirmed,
                  confirmedGeneration: existing?.confirmedGeneration ?? 0,
                  generation: (existing?.generation ?? 0) + 1,
                  inFlight: existing?.inFlight ?? false,
                  desired: existing?.desired,
                  surface: existing?.surface,
                });
              } else if (existing?.confirmed !== undefined) {
                current.resolved.set(propertyId, existing.confirmed);
              }
              current.failed.delete(propertyId);
            }
          } catch {
            const current = searchRegistry.current;
            if (generation === current.generation && enabledRef.current) {
              chunk.forEach((propertyId) => {
                if (
                  current.candidateIds.has(propertyId) &&
                  !current.resolved.has(propertyId)
                ) {
                  current.failed.add(propertyId);
                }
              });
            }
          } finally {
            const current = searchRegistry.current;
            if (generation === current.generation) {
              chunk.forEach((propertyId) => current.loading.delete(propertyId));
              if (current.failed.size === 0 && current.loading.size === 0) {
                current.retryAttempts = 0;
              }
              rerender();
            }
          }
        })
      );
    },
    [rerender]
  );

  const registerSearchCandidates = useCallback(
    (
      searchKey: string,
      propertyIds: number[],
      options: { terminal?: boolean; replace?: boolean } = {}
    ): void => {
      if (!enabledRef.current) return;
      const candidateIds = new Set(
        propertyIds.filter(
          (propertyId) => Number.isSafeInteger(propertyId) && propertyId > 0
        )
      );
      let registry = searchRegistry.current;
      const isNewSearch = registry.key !== searchKey;
      const isCanonicalReplacement =
        options.replace === true && !registry.canonicalized;

      if (isNewSearch || isCanonicalReplacement) {
        const priorResolved = isNewSearch
          ? new Map<number, boolean>()
          : registry.resolved;
        registry = {
          key: searchKey,
          generation: registry.generation + 1,
          canonicalized: options.replace === true,
          candidateIds: new Set(candidateIds),
          resolved: new Map(
            Array.from(candidateIds)
              .filter((propertyId) => priorResolved.has(propertyId))
              .map((propertyId) => [
                propertyId,
                priorResolved.get(propertyId) as boolean,
              ])
          ),
          loading: new Set(),
          failed: new Set(),
          retryAttempts: 0,
          terminal: options.terminal === true,
        };
        searchRegistry.current = registry;
      } else {
        candidateIds.forEach((propertyId) =>
          registry.candidateIds.add(propertyId)
        );
        if (options.terminal === true) registry.terminal = true;
      }

      resolveKnownSearchCandidates(
        registry,
        membership.current,
        registry.candidateIds
      );
      const hasNewCandidates = Array.from(registry.candidateIds).some(
        (propertyId) =>
          !registry.resolved.has(propertyId) &&
          !registry.loading.has(propertyId) &&
          !registry.failed.has(propertyId)
      );
      if (hasNewCandidates && registry.failed.size > 0) {
        registry.failed.clear();
        registry.retryAttempts = 0;
      }
      const unresolved = Array.from(registry.candidateIds).filter(
        (propertyId) =>
          !registry.resolved.has(propertyId) &&
          !registry.loading.has(propertyId) &&
          !registry.failed.has(propertyId)
      );
      rerender();
      if (unresolved.length > 0) {
        void resolveSearchMembership(unresolved, registry.generation);
      }
    },
    [rerender, resolveSearchMembership]
  );

  const requestFailedSearchMembership = useCallback((): void => {
    if (!enabledRef.current) return;
    const registry = searchRegistry.current;
    registry.failed.clear();
    resolveKnownSearchCandidates(
      registry,
      membership.current,
      registry.candidateIds
    );
    const unresolved = Array.from(registry.candidateIds).filter(
      (propertyId) =>
        !registry.resolved.has(propertyId) && !registry.loading.has(propertyId)
    );
    rerender();
    if (unresolved.length > 0) {
      void resolveSearchMembership(unresolved, registry.generation);
    }
  }, [rerender, resolveSearchMembership]);

  const retrySearchMembership = useCallback((): void => {
    searchRegistry.current.retryAttempts = 0;
    requestFailedSearchMembership();
  }, [requestFailedSearchMembership]);

  const resetSearchMembership = useCallback((): void => {
    const generation = searchRegistry.current.generation + 1;
    searchRegistry.current = {
      generation,
      canonicalized: false,
      candidateIds: new Set(),
      resolved: new Map(),
      loading: new Set(),
      failed: new Set(),
      retryAttempts: 0,
      terminal: false,
    };
    rerender();
  }, [rerender]);

  const pumpMutation = useCallback(
    async (propertyId: number): Promise<void> => {
      const entry = membership.current.get(propertyId);
      if (!entry || entry.inFlight || entry.confirmed === undefined) return;

      while (
        enabledRef.current &&
        entry.desired !== undefined &&
        entry.desired !== entry.confirmed
      ) {
        const generation = sessionGeneration.current;
        const mutationGeneration = entry.generation;
        const target: boolean = entry.desired;
        const surface = entry.surface;
        entry.inFlight = true;
        rerender();

        try {
          const response = await fetch(
            `/api/search/hotel/favorites/${propertyId}`,
            { method: target ? "PUT" : "DELETE", cache: "no-store" }
          );
          if (!response.ok) throw new Error("favorite mutation failed");
          const raw: unknown = await response.json();
          const added = target ? FavoriteHotelFromJSON(raw) : undefined;
          if (!target) FavoriteHotelMutationResponseFromJSON(raw);
          if (generation !== sessionGeneration.current || !enabledRef.current) {
            return;
          }

          entry.confirmed = target;
          entry.confirmedGeneration = mutationGeneration;
          resolveKnownSearchCandidates(
            searchRegistry.current,
            membership.current,
            [propertyId]
          );
          setHistory((current) => ({
            ...current,
            items: target
              ? [
                  added as FavoriteHotel,
                  ...current.items.filter(
                    (item) => item.propertyId !== propertyId
                  ),
                ]
              : current.items.filter((item) => item.propertyId !== propertyId),
          }));
          if (surface) {
            captureFavoriteEvent(
              posthogRef.current,
              target ? FAVORITE_ADD : FAVORITE_REMOVE,
              propertyId,
              surface
            );
          }
          if (entry.generation === mutationGeneration) {
            entry.desired = undefined;
          }
        } catch {
          if (
            generation === sessionGeneration.current &&
            enabledRef.current &&
            entry.generation === mutationGeneration
          ) {
            entry.desired = undefined;
            setError(FAVORITE_MUTATION_ERROR);
            errorToast(FAVORITE_MUTATION_ERROR);
          }
        } finally {
          entry.inFlight = false;
          rerender();
        }
      }

      if (entry.desired === entry.confirmed) {
        entry.desired = undefined;
        rerender();
      }
    },
    [rerender]
  );

  const toggleFavorite = useCallback(
    (propertyId: number, surface: FavoriteSurface): void => {
      if (!enabledRef.current) return;
      const entry = membership.current.get(propertyId);
      if (!entry || entry.confirmed === undefined) return;

      const effective = entry.desired ?? entry.confirmed;
      entry.desired = !effective;
      entry.surface = surface;
      entry.generation += 1;
      setError(null);
      rerender();
      if (!entry.inFlight) void pumpMutation(propertyId);
    },
    [pumpMutation, rerender]
  );

  const favoriteState = useCallback(
    (propertyId: number): HotelFavoriteState => {
      if (storeIdentity.current !== identity) {
        return { isFavorite: undefined, pending: false };
      }
      const entry = membership.current.get(propertyId);
      return {
        isFavorite: entry ? entry.desired ?? entry.confirmed : undefined,
        pending: Boolean(
          entry?.inFlight ||
            (entry?.desired !== undefined && entry.desired !== entry.confirmed)
        ),
      };
    },
    [identity]
  );

  useLayoutEffect(() => {
    enabledRef.current = enabled;
    posthogRef.current = posthog;
    if (storeIdentity.current === identity) return;

    storeIdentity.current = identity;
    sessionGeneration.current += 1;
    membership.current.clear();
    membershipLoading.current.clear();
    historyRequests.current.clear();
    searchRegistry.current = {
      generation: searchRegistry.current.generation + 1,
      canonicalized: false,
      candidateIds: new Set(),
      resolved: new Map(),
      loading: new Set(),
      failed: new Set(),
      retryAttempts: 0,
      terminal: false,
    };
  }, [enabled, identity, posthog]);

  useEffect(() => {
    if (enabled) void refreshHistory();
  }, [enabled, refreshHistory, subject]);

  const registry =
    storeIdentity.current === identity ? searchRegistry.current : undefined;
  const isComplete = registry
    ? Array.from(registry.candidateIds).every((propertyId) =>
        registry.resolved.has(propertyId)
      )
    : true;
  const effectiveFavoriteIds = new Set<number>();
  registry?.candidateIds.forEach((propertyId) => {
    if (!registry.resolved.has(propertyId)) return;
    const entry = membership.current.get(propertyId);
    const effective = entry?.desired ?? entry?.confirmed ?? false;
    if (effective) effectiveFavoriteIds.add(propertyId);
  });
  const failedCount = registry?.failed.size ?? 0;
  const retryAttempts = registry?.retryAttempts ?? 0;
  const retryGeneration = registry?.generation;
  const retriesExhausted = retryAttempts >= membershipRetryDelaysMs.length;

  useEffect(() => {
    if (failedCount === 0) return;
    const delay = membershipRetryDelaysMs[retryAttempts];
    if (delay === undefined) return;
    const timer = setTimeout(() => {
      const current = searchRegistry.current;
      if (current.generation !== retryGeneration) return;
      current.retryAttempts += 1;
      requestFailedSearchMembership();
    }, delay);
    return () => clearTimeout(timer);
  }, [
    failedCount,
    membershipRetryDelaysMs,
    requestFailedSearchMembership,
    retryAttempts,
    retryGeneration,
  ]);

  const searchMembership: FavoriteSearchMembershipState = {
    status: !enabled
      ? "disabled"
      : !registry?.key
        ? "idle"
        : failedCount > 0 && retriesExhausted
          ? "error"
          : failedCount > 0 || !registry.terminal || !isComplete
            ? "loading"
            : "ready",
    isComplete,
    isTerminal: registry?.terminal ?? false,
    effectiveFavoriteIds,
    candidateIds: new Set(registry?.candidateIds ?? []),
  };

  const value: FavoriteHotelsContextValue = {
    enabled,
    subject,
    history,
    error,
    ensureMembership,
    searchMembership,
    registerSearchCandidates,
    retrySearchMembership,
    resetSearchMembership,
    refreshHistory,
    loadMoreHistory,
    retryHistory,
    favoriteState,
    toggleFavorite,
  };

  return (
    <FavoriteHotelsContext.Provider value={value}>
      {children}
    </FavoriteHotelsContext.Provider>
  );
}

export function useFavoriteHotels(): FavoriteHotelsContextValue {
  const context = useContext(FavoriteHotelsContext);
  if (!context) {
    throw new Error(
      "useFavoriteHotels must be used within FavoriteHotelsProvider"
    );
  }
  return context;
}

export function useOptionalFavoriteHotels(): FavoriteHotelsContextValue | null {
  return useContext(FavoriteHotelsContext);
}
